<template>
	<div
		class="calculator__customizeritem"
		:class="`calculator__customizeritem--${share.key} calculator__customizeritem--l${level}`"
	>
		<p v-if="text" class="calculator__customizeritem-text" v-html="text"></p>

		<div class="calculator__customizeritem-inner">
			<div class="calculator__customizeritem-toggle">
				<div class="calculator__customizeritem-toggle-inner">
					<span
						class="calculator__customizeritem-title"
						:class="{ 'calculator__customizeritem-title--deceased': share.isDeceased }"
						>{{ share.title }}</span
					>

					<SwitchButtons v-if="modes" :options="modes" :onSwitch="onSwitchMode" />
				</div>
			</div>

			<div class="calculator__customizeritem-value">
				<div v-if="!share.isAbsolute" class="calculator__customizeritem-slider">
					<Slider
						:value="share.value"
						:disabled="isAutomated"
						:name="`_calculator-value-${share.key}`"
						:label="$t('customizeValue')"
						class="calculator__customizeritem-range"
						:class="{ 'calculator__customizeritem-range--disabled': isAutomated }"
						:style="{ '--color': share.color }"
						:indicators="indicators"
						:showCalculatorError="showCalculatorError"
						@change="onChange"
					/>

					<InputField
						v-if="isFixable"
						:value="share.value"
						:name="`_calculator-value-${share.key}-number`"
						:label="$t('customizeValue')"
						:readonly="share.isFixed"
						class="calculator__customizeritem-range-input-direct"
						type="number"
						min="0"
						max="100"
						step="0.01"
						@input="onChangeDebounced"
					/>
				</div>

				<InputField
					v-else
					:value="share.absoluteValue"
					:name="`_calculator-value-${share.key}`"
					:label="$t('customizeAbsoluteValue')"
					:required="true"
					type="number"
					class="calculator__customizeritem-number"
					@input="onChangeAbsolute"
					@keydown.prevent.enter
				/>

				<div v-if="isFixable" class="calculator__customizeritem-fix">
					<button
						:aria-pressed="share.isFixed"
						class="mdc-button ln-tooltip__trigger"
						type="button"
						@click="onFix"
					>
						<i
							v-if="share.isFixed"
							class="material-icons mdc-button__icon"
							role="presentation"
							>lock</i
						>
						<i
							v-if="!share.isFixed"
							class="material-icons mdc-button__icon"
							role="presentation"
							>lock_open</i
						>
						<span class="ln-u-visuallyhidden">
							{{ $t('customizeFix') }}
						</span>
					</button>

					<span class="ln-tooltip__content">
						{{ $t('customizeFix') }}
					</span>
				</div>
			</div>
		</div>

		<ul v-if="share.parts" class="calculator__customizeritem-parts">
			<li v-for="part in share.parts" :key="part.key" class="calculator__customizer-item">
				<CustomizerItem :share="part" :level="level + 1" />
			</li>
		</ul>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import Slider from './Slider.vue';
import InputField from './InputField.vue';
import SwitchButtons from './SwitchButtons.vue';

export default {
	name: 'CustomizerItem',
	components: {
		Slider,
		InputField,
		SwitchButtons
	},
	props: {
		share: Object,
		level: {
			type: Number,
			default: 0
		}
	},
	computed: {
		isFixable() {
			const isFixable =
				this.share.key !== 'free' && !this.share.parts && !this.share.isAbsolute;

			return isFixable;
		},
		isAutomated() {
			const isAutomated = this.share.isFixed || !this.isFixable;

			return isAutomated;
		},
		indicators() {
			const indicators = this.share.isIndicated
				? [
						{
							value: this.share.compulsoryValue,
							label: this.$t('customizeValueCompulsory')
						}
				  ] // eslint-disable-line no-mixed-spaces-and-tabs
				: [];

			return indicators;
		},
		showCalculatorError() {
			const showCalculatorError = this.share.key === 'free';

			return showCalculatorError;
		},
		modes() {
			if (!this.share.isSwitchableAbsolute) {
				return null;
			}

			const modes = [
				{
					key: 'absolute',
					label: this.$t('customizeAbsoluteShare'),
					hint: this.$t('customizeAbsoluteShareHint'),
					isSelected: this.share.isAbsolute
				},
				{
					key: 'relative',
					label: this.$t('customizeRelativeShare'),
					hint: this.$t('customizeRelativeShareHint'),
					isSelected: !this.share.isAbsolute
				}
			];

			return modes;
		},
		text() {
			const text = this.$t(`${this.share.key}Hint`);

			return text;
		}
	},
	methods: {
		onChange($event) {
			if ($event.isTrusted) {
				this.$store.commit('setShareValue', {
					key: this.share.key,
					targetValue: parseFloat($event.target.value)
				});
			}
		},
		onChangeDebounced: debounce(function ($event) {
			this.onChange($event);
		}, 500),
		onFix($event) {
			const isPressed = $event.currentTarget.getAttribute('aria-pressed') === 'true';

			this.$store.commit('setShareFixed', {
				key: this.share.key,
				isFixed: !isPressed
			});
		},
		onChangeAbsolute($event) {
			this.$store.commit('setShareAbsoluteValue', {
				key: this.share.key,
				targetValue: parseFloat($event.target.value) || 0
			});
		},
		onSwitchMode(modeKey) {
			const isAbsolute = modeKey === 'absolute';

			this.$store.commit('setShareAbsolute', {
				key: this.share.key,
				isAbsolute
			});
		}
	}
};
</script>

<style>
@import url('../assets/css/variables.css');

.calculator__customizeritem-toggle-inner {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	padding-top: 1rem;
	padding-bottom: 1rem;
}

.calculator__customizeritem-title {
	font-weight: bold;
}

.calculator__customizeritem-title--deceased {
	color: rgba(0, 0, 0, 0.5);
	text-decoration: line-through;
}

.calculator__customizeritem-value {
	flex-basis: 50%;
	box-sizing: border-box;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 8px;
	margin-bottom: 16px;
}
.calculator__customizeritem-slider {
	width: calc(100% - 40px);
	display: flex;
}
.calculator__customizeritem-range {
	width: calc(100% - 100px);
	z-index: 0; /* Make sure the slider indicators (`z-index: -1`) are visible */
}
.calculator__customizeritem-range .mdc-slider__pin {
	display: none;
}
.calculator__customizeritem-range--disabled ._mdc-slider-wrapper {
	opacity: 0.5;
	pointer-events: none;
}
.calculator__customizeritem-range-input {
	width: 100%;
}
.calculator__customizeritem-range-input-direct {
	width: 80px;
	margin-left: auto;
}
.calculator__customizeritem-range-input-direct .mdc-notched-outline__notch {
	width: 0 !important;
	padding: 0;
	overflow: hidden;
}
.calculator__customizeritem-range-input-direct .mdc-text-field-helper-line {
	white-space: nowrap;
}
.calculator__customizeritem-number {
	width: calc(100% - 40px);
}
.calculator__customizeritem-fix {
	position: relative;
}

/* Custom shares */
.calculator__customizeritem--custom {
	border-top: 1px solid var(--color-line);
	margin-top: 24px;
}

/* Free share */
.calculator__customizeritem--free {
	border-top: 1px solid var(--color-line);
	padding-top: 8px;
	margin-top: 24px;
}
.calculator__customizeritem--free label {
	font-weight: bold;
}

/* Sub shares */
.calculator__customizeritem-parts {
	list-style: none;
	margin: 0;
	padding: 0;
}

.calculator__customizeritem--l1 .calculator__customizeritem-title {
	font-weight: normal;
}

.calculator__customizeritem--l1 .calculator__customizeritem-toggle-inner,
.calculator__customizeritem--l1 .calculator__customizeritem-value {
	padding-left: 20px;
}
.calculator__customizeritem--l2 .calculator__customizeritem-toggle-inner,
.calculator__customizeritem--l2 .calculator__customizeritem-value {
	padding-left: 40px;
}
.calculator__customizeritem--l3 .calculator__customizeritem-toggle-inner,
.calculator__customizeritem--l3 .calculator__customizeritem-value {
	padding-left: 60px;
}

/* Desktop */
@media (min-width: 64rem) {
	.calculator__customizeritem-inner {
		display: flex;
		align-items: flex-start;
	}

	.calculator__customizeritem-toggle {
		flex-basis: 50%;
		padding-right: var(--spacing);
		box-sizing: border-box;
	}
	.calculator__customizeritem-toggle .calculator__switch {
		margin-right: calc(0px - var(--spacing));
	}

	.calculator__customizeritem-value.calculator__customizeritem-value {
		margin-top: 0;
		padding-left: var(--spacing);
	}

	.calculator__customizeritem-number {
		width: calc(100% - 160px);
	}
}

/* Overwrite default app styles */
.calculator__customizeritem-toggle-inner .mdc-button {
	min-width: 65px;
	margin-top: -16px;
	flex-shrink: 0;
}
.calculator__customizeritem-toggle-inner .mdc-button__icon.mdc-button__icon {
	margin-right: 0;
}
.calculator__customizeritem-toggle-inner > .mdc-button.mdc-button {
	color: var(--color-error);
}
.calculator__customizeritem-value .mdc-button[aria-pressed='true'] {
	color: var(--color-warning);
}
.calculator__customizeritem-fix .mdc-button {
	min-width: 0;
}
.calculator__customizeritem-fix .mdc-button__icon {
	margin-right: 0;
}

@media (max-width: 64rem) {
	.ln-tooltip__content {
		transform: none;
		left: auto;
		right: 0;
	}
}
</style>
